'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {
  AppIcon,
  FaBankIcon,
  FaBriefcaseIcon,
  FaCarIcon,
  FaFlightIcon,
  FaGraduationCapIcon,
  FaMedicalIcon,
  FaPaletteIcon,
  FaRestaurantIcon,
  FaRunIcon,
  FaShoppingBagIcon,
  FaShoppingCartIcon,
  FaTrainIcon,
  type IconDefinition,
} from '../icon';
import {Typography} from '../primitives';

// Nearby "what's nearby" category → glyph. Domain presentation for this row —
// the content model carries the category key, the mapping lives here. Unknown
// keys fall back to the default travel-mode glyph (car).
const NEARBY_ICONS: Record<string, IconDefinition> = {
  directions_car: FaCarIcon,
  directions_run: FaRunIcon,
  local_hospital: FaMedicalIcon,
  school: FaGraduationCapIcon,
  restaurant: FaRestaurantIcon,
  shopping_bag: FaShoppingBagIcon,
  shopping_cart: FaShoppingCartIcon,
  train: FaTrainIcon,
  flight: FaFlightIcon,
  account_balance: FaBankIcon,
  business_center: FaBriefcaseIcon,
  palette: FaPaletteIcon,
};
const DEFAULT_NEARBY_ICON: IconDefinition = FaCarIcon;

export type NearbyPlaceRowColor = 'light' | 'dark';

export interface NearbyPlaceRowProps {
  icon?: string;
  label: string;
  time: string;
  color?: NearbyPlaceRowColor;
}

const Root = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
}));

const TimeRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ROW_TOKENS = {
  light: {label: 'inverse', time: 'inverseMuted', icon: 'muted'},
  dark: {label: 'strong', time: 'default', icon: 'default'},
} as const;

export function NearbyPlaceRow({icon = 'directions_car', label, time, color = 'dark'}: NearbyPlaceRowProps) {
  const placeIcon = NEARBY_ICONS[icon] ?? DEFAULT_NEARBY_ICON;
  const tokens = ROW_TOKENS[color];
  return (
    <Root>
      <Typography
        size="body-2"
        weight="medium"
        family="primary"
        as="span"
        sx={{letterSpacing: '-0.015em', color: theme => theme.palette.colors.text.neutral[tokens.label]}}
      >
        {label}
      </Typography>
      <TimeRow>
        <AppIcon icon={placeIcon} size="sm" sx={{color: theme => theme.palette.colors.icon.neutral[tokens.icon]}} />
        <Typography size="body-2" weight="regular" as="span" sx={{color: theme => theme.palette.colors.text.neutral[tokens.time]}}>
          {time}
        </Typography>
      </TimeRow>
    </Root>
  );
}

NearbyPlaceRow.displayName = 'NearbyPlaceRow';
